/** @jsxImportSource @emotion/react */

import { useLingui } from '@lingui/react/macro';
import { Copy } from 'lucide-react';
import { getSportTypeLabel } from 'modules/core/domain/constants/sport-type-labels';

import React from 'react';
import { toast } from 'react-toastify';
import { theme } from 'styles/theme/theme';
import EmptyButton from 'ud-ui/components/empty-button';
import Label from 'ud-ui/components/label';
import UDText from 'ud-ui/components/text';
import UDTippy from 'ud-ui/components/tippy';
import { OrganizerDetails } from 'modules/organizers/domain/interfaces/organizer-details';
import PlansBlock from 'modules/organizers/ui/components/plans-block';
import { CustomField, CustomTextarea, disableColor, InfoContainer } from './styles';

interface OrganizerInfoProps {
  organizer: OrganizerDetails | undefined;
}

const OrganizerInfo = React.memo(function OrganizerInfo({ organizer }: OrganizerInfoProps) {
  const { t } = useLingui();
  const sportTypeLabel = getSportTypeLabel(organizer?.sportType);
  const copyTooltip = t`Скопировать`;

  const onClickCopy = (text: string) => {
    navigator.clipboard.writeText(text).then((_) => {
      toast.success(t`Сохранено в буфер обмена!`);
    });
  };

  return (
    <InfoContainer>
      <div className="w-1/2">
        <Label text={t`Название организатора`} className={'mb-4'}>
          <CustomField>
            <UDText type={'subhead'}>{organizer?.name}</UDText>
            {organizer?.name && (
              <UDTippy content={copyTooltip} size={'xs'}>
                <EmptyButton color={theme.colors.Gray} onClick={() => onClickCopy(organizer?.name ?? '')} icon={<Copy size={14} />} />
              </UDTippy>
            )}
          </CustomField>
        </Label>
        <Label text={t`Тип спорта`} className={'mb-4'}>
          <CustomField>
            <UDText type={'subhead'}>{sportTypeLabel ? t(sportTypeLabel) : organizer?.sportType}</UDText>
          </CustomField>
        </Label>
        <Label text="Email" className={'mb-4'}>
          <CustomField>
            <UDText type={'subhead'}>{organizer?.email}</UDText>
            {organizer?.email && (
              <UDTippy content={copyTooltip} size={'xs'}>
                <EmptyButton color={theme.colors.Gray} onClick={() => onClickCopy(organizer?.email ?? '')} icon={<Copy size={14} />} />
              </UDTippy>
            )}
          </CustomField>
        </Label>
        <Label text={t`Телефон`} className={'mb-4'}>
          <CustomField>
            <UDText type={'subhead'}>{organizer?.phone}</UDText>
            {organizer?.phone && (
              <UDTippy content={copyTooltip} size={'xs'}>
                <EmptyButton color={theme.colors.Gray} onClick={() => onClickCopy(organizer?.phone ?? '')} icon={<Copy size={14} />} />
              </UDTippy>
            )}
          </CustomField>
        </Label>
        <Label text={t`Город`} className={'mb-4'}>
          <CustomField>
            <UDText type={'subhead'}>{organizer?.address.city}</UDText>
          </CustomField>
        </Label>
        <Label text={t`Описание`} className={'mb-4'}>
          <CustomTextarea css={disableColor} readOnly rows={4} value={organizer?.description} />
        </Label>
      </div>
      <div className="w-1/2">
        <Label text={t`Информация о подписке организатора`}>
          <PlansBlock tournamentId={organizer?.id ?? 0} owner={organizer?.adminUser} />
        </Label>
      </div>
    </InfoContainer>
  );
});

export default OrganizerInfo;
